// src/utils/paymentFlag.js
// Camada central de "pagou ou não pagou" — amarrada ao patientId.
//
// Motivação: antes, `paymentConfirmed='1'` ficava solto no localStorage e
// vazava entre consultas. No caso Maria Eduarda Palagi (2026-05-04), o flag
// da consulta de 30/04 permaneceu '1' e, quando ela criou novo patientId
// para nova consulta, o ConsultaChat escondeu a barra "💳 Pagar" achando
// que ela já tinha pago. Ela ficou sem conseguir pagar pelo app.
//
// Agora o flag só é válido se ESTIVER ligado ao patientId atual. Se o
// patientId muda, a leitura retorna false automaticamente, mesmo que
// `paymentConfirmed` ainda esteja como '1' no storage por qualquer motivo.

import { purgeKeysFromBackup } from './sessionBackup';

const KEY_FLAG = 'paymentConfirmed';
const KEY_FOR = 'paymentConfirmedFor';
const KEY_AT = 'paymentConfirmedAt';

const EMULATOR_URL = 'http://localhost:5000';
const DEFAULT_URL =
  typeof window !== 'undefined' ? window.location.origin : '';
const BASE_URL =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL) ||
  ((typeof process !== 'undefined' &&
    process.env &&
    process.env.NODE_ENV === 'development')
    ? EMULATOR_URL
    : DEFAULT_URL);

const read = (key) => {
  try {
    const v = localStorage.getItem(key);
    if (v != null) return v;
  } catch {}
  try {
    const v = sessionStorage.getItem(key);
    if (v != null) return v;
  } catch {}
  return null;
};

const write = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {}
  try {
    sessionStorage.setItem(key, value);
  } catch {}
};

const remove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {}
  try {
    sessionStorage.removeItem(key);
  } catch {}
};

/**
 * Marca o pagamento como confirmado para o patientId informado.
 * Exige patientId — escrita sem patientId é considerada bug e ignorada.
 */
export function markPaymentConfirmed(patientId) {
  const pid = String(patientId || '').trim();
  if (!pid) {
    if (typeof console !== 'undefined') {
      console.warn('[paymentFlag] markPaymentConfirmed chamado sem patientId — ignorado');
    }
    return false;
  }
  write(KEY_FLAG, '1');
  write(KEY_FOR, pid);
  write(KEY_AT, new Date().toISOString());
  return true;
}

/**
 * Retorna true SOMENTE se o flag atual foi gravado para este patientId.
 * Qualquer descasamento (patientId diferente, sentinela ausente) → false.
 */
export function isPaymentConfirmedFor(patientId) {
  const pid = String(patientId || '').trim();
  if (!pid) return false;
  const flag = read(KEY_FLAG);
  if (flag !== '1') return false;
  const boundTo = read(KEY_FOR);
  if (!boundTo) return false;
  return boundTo === pid;
}

/**
 * Limpa TODAS as chaves relacionadas ao flag de pagamento.
 * Também limpa `payerName` e `conversionTracked` pois são derivados do flag.
 *
 * Limpa igualmente o snapshot de backup: sem isso o `restoreSession` reescreve
 * `paymentConfirmed='1'` do IndexedDB no boot seguinte (o binding com o
 * patientId bate, então a defesa anti-vazamento não barra) e o paciente
 * estornado volta a ficar sem a barra "Pagar".
 */
export function clearPaymentConfirmation() {
  remove(KEY_FLAG);
  remove(KEY_FOR);
  remove(KEY_AT);
  remove('payerName');
  remove('conversionTracked');
  try {
    const purge = purgeKeysFromBackup([KEY_FLAG, KEY_FOR, KEY_AT]);
    if (purge && typeof purge.catch === 'function') purge.catch(() => {});
  } catch {}
}

/**
 * Status do backend que significam dinheiro devolvido ao paciente — de estorno
 * pedido no app, pelo médico ou pela Sofia até chargeback/disputa resolvida
 * contra nós. Em todos, o paciente precisa poder pagar de novo pelo botão.
 * Mesma régua do painel do médico (`doctorPaymentStatusCache`).
 */
const REFUND_LIKE_STATUSES = new Set([
  'refunded',
  'reversed',
  'chargedback',
  'chargeback',
  'charged_back',
  'disputed',
]);

/**
 * Aviso automático de devolução publicado no chat pelo backend
 * (`notifyPaymentRefunded`): "↩️ **Reembolso Realizado**", "↩️ **Estorno
 * Realizado**", "🚨 **Chargeback Detectado!**", "🚨 **Disputa Detectado!**".
 *
 * Serve só como GATILHO para revalidar no servidor — quem decide se o flag cai
 * é sempre o `/payments/status`. Exige o negrito do template e ancora no início
 * para que mensagem digitada pelo médico mencionando reembolso não dispare.
 */
const REFUND_NOTICE_RX =
  /^\s*(?:↩️|🚨)?\s*\*\*(?:Reembolso|Estorno|Chargeback|Disputa)\s+(?:Realizado|Detectado)!?\*\*/;

export function isRefundNoticeText(text) {
  if (!text) return false;
  return REFUND_NOTICE_RX.test(String(text));
}

/**
 * Consulta o backend só para saber se o pagamento foi devolvido.
 *
 * Fail-safe deliberado: qualquer resposta ambígua (HTTP != 2xx, JSON
 * inválido, erro de rede, ausência de fetch) devolve `false`, mantendo o
 * paciente com acesso pago. Só um status explícito de devolução derruba o
 * flag — o custo de errar para o outro lado é reexibir paywall para quem
 * pagou.
 */
async function isRefundedOnBackend(pid, { fetchFn, baseUrl } = {}) {
  const f = fetchFn || (typeof fetch !== 'undefined' ? fetch : null);
  if (!f) return false;
  try {
    const res = await f(
      `${baseUrl || BASE_URL}/payments/status?userId=${encodeURIComponent(pid)}`
    );
    if (!res || !res.ok) return false;
    const data = await res.json();
    if (!data || data.paid === true) return false;
    return REFUND_LIKE_STATUSES.has(String(data.status || '').toLowerCase());
  } catch {
    return false;
  }
}

/**
 * Reconciliação com o backend (fonte de verdade).
 *
 * Chamar no boot de qualquer tela que depende do flag (ConsultaChat,
 * PatientLayout). Resolve 4 cenários:
 *
 *  1) Legado: flag='1' sem paymentConfirmedFor (pacientes que pagaram
 *     ANTES do deploy da Fase 2). Valida com /payments/status. Se pago,
 *     faz bind ao patientId atual; se não, limpa.
 *
 *  2) Descasamento: flag='1' e paymentConfirmedFor=<outro_patientId>.
 *     Limpa IMEDIATAMENTE — nunca foi desta consulta.
 *
 *  3) Bind correto: flag='1' e paymentConfirmedFor=patientId atual.
 *     Confirma no backend que o pagamento não foi estornado; se foi, limpa.
 *
 *  4) Sem flag local: consulta /payments/status. Se backend confirma pago
 *     (ex.: checkout MP na mesma aba sem passar por /pagamento), grava flag.
 *
 * Retorna { paid, source, cleared, payerName } — informativo, caller pode logar.
 */
export async function migrateLegacyFlag(patientId, { fetchFn, baseUrl } = {}) {
  const pid = String(patientId || '').trim();
  const result = { paid: false, source: 'none', cleared: false, payerName: null };
  if (!pid) return result;

  const flag = read(KEY_FLAG);
  const boundTo = read(KEY_FOR);

  // Cenário 2: descasamento explícito — limpa sem consultar backend.
  if (flag === '1' && boundTo && boundTo !== pid) {
    clearPaymentConfirmation();
    result.cleared = true;
    result.source = 'mismatch';
    return result;
  }

  // Cenário 3: bind correto. O flag local não basta — estorno feito pelo
  // médico no dashboard, pela Sofia no suporte, pelo worker agendado ou
  // direto no painel do gateway não passa pelo app, então sem esta checagem
  // o paciente estornado continua vendo o chat como pago, sem a barra
  // "💳 Pagar", e não consegue pagar de novo na mesma consulta (caso Aline
  // Santos, 16/08/2026). Antes, só o self-refund intrachat limpava o flag,
  // e ele foi retirado.
  if (flag === '1' && boundTo === pid) {
    const refunded = await isRefundedOnBackend(pid, { fetchFn, baseUrl });
    if (refunded) {
      clearPaymentConfirmation();
      result.cleared = true;
      result.source = 'bound_backend_refunded';
      return result;
    }
    result.paid = true;
    result.source = 'bound';
    return result;
  }

  // Cenário 1: legado (flag='1' sem boundTo). Consulta backend.
  if (flag === '1' && !boundTo) {
    const url = `${baseUrl || BASE_URL}/payments/status?userId=${encodeURIComponent(pid)}`;
    try {
      const f = fetchFn || (typeof fetch !== 'undefined' ? fetch : null);
      if (!f) return result;
      const res = await f(url);
      if (!res || !res.ok) {
        // Backend indisponível: mantém flag por enquanto (não piorar UX).
        // Bind ao patientId atual para não re-tentar a cada boot.
        write(KEY_FOR, pid);
        result.paid = true;
        result.source = 'legacy_backend_unavailable';
        return result;
      }
      const data = await res.json();
      if (data && data.paid === true) {
        // Confirmado: bind.
        markPaymentConfirmed(pid);
        result.paid = true;
        result.source = 'legacy_backend_confirmed';
        return result;
      }
      // Backend diz que NÃO pagou → vazamento. Limpa.
      clearPaymentConfirmation();
      result.cleared = true;
      result.source = 'legacy_backend_denied';
      return result;
    } catch (err) {
      // Erro de rede: conservador — não limpa, mas faz bind para não
      // ficar re-consultando.
      write(KEY_FOR, pid);
      result.paid = true;
      result.source = 'legacy_network_error';
      return result;
    }
  }

  // Cenário 4: sem flag local — backend é fonte de verdade.
  if (flag !== '1') {
    const url = `${baseUrl || BASE_URL}/payments/status?userId=${encodeURIComponent(pid)}`;
    try {
      const f = fetchFn || (typeof fetch !== 'undefined' ? fetch : null);
      if (!f) return result;
      const res = await f(url);
      if (!res || !res.ok) return result;
      const data = await res.json();
      if (data && data.paid === true) {
        markPaymentConfirmed(pid);
        if (data.payerName) {
          write('payerName', data.payerName);
          result.payerName = data.payerName;
        }
        result.paid = true;
        result.source = 'backend_confirmed_no_flag';
      }
    } catch {
      // Rede instável: mantém unpaid; polling ou próximo boot tenta de novo.
    }
  }

  return result;
}

/** Chaves usadas — exportadas para scripts de limpeza / sessionBackup. */
export const PAYMENT_FLAG_KEYS = [KEY_FLAG, KEY_FOR, KEY_AT];

